import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { WidgetData, WidgetType } from "../models/dashboard.model";
import { EntityApiService } from "../../../shared/store/entity-store/entity-api.service";
import { map, Observable } from "rxjs";
import {
  Identifiable,
  QueryParam,
} from "../../../shared/store/store-types.model";
import {
  EVOLUTION_VALEUR_DATA,
  EvolutionDimension,
  EvolutionGranularity,
  COUVERTURE_COMMANDES_DATA,
} from "../models/data.model";

@Injectable({
  providedIn: "root",
})
export class WidgetDataApiService implements EntityApiService<WidgetData> {
  constructor(private http: HttpClient) {}

  loadAll(query?: QueryParam | undefined): Observable<WidgetData[]> {
    throw new Error("Method not implemented.");
  }

  create(entity: WidgetData): Observable<WidgetData> {
    throw new Error("Method not implemented.");
  }

  remove(id: string): Observable<string> {
    throw new Error("Method not implemented.");
  }

  update(entity: WidgetData): Observable<WidgetData> {
    throw new Error("Method not implemented.");
  }

  loadOne(type: WidgetType): Observable<WidgetData> {
    return this.http
      .get<Identifiable>(`${environment.api()}/widget/type/${type}`)
      .pipe(
        // delay(5000),
        map((res: any) => {
          return { data: res, id: type, type } as WidgetData;
        }),
      );
  }

  /** Vide le cache widget côté back. Le prochain `loadOne` recalculera. */
  refreshAll(): Observable<{ cleared: number }> {
    return this.http.post<{ cleared: number }>(
      `${environment.api()}/widget/refresh`,
      {},
    );
  }

  /**
   * Charge les séries temporelles pour le widget EVOLUTION_VALEUR. Endpoint
   * dédié (pas le pipeline générique `/widget/type/:type`) car nécessite des
   * paramètres de plage et de granularité.
   */
  loadEvolutionValeur(
    granularity: EvolutionGranularity,
    dimension: EvolutionDimension,
    startDate: string,
    endDate: string,
  ): Observable<EVOLUTION_VALEUR_DATA> {
    const params = new HttpParams()
      .set("granularity", granularity)
      .set("dimension", dimension)
      .set("startDate", startDate)
      .set("endDate", endDate);
    return this.http.get<EVOLUTION_VALEUR_DATA>(
      `${environment.api()}/widget/evolution-valeur`,
      { params },
    );
  }

  /**
   * Widget "Couverture des commandes" — endpoint dédié paramétrable par
   * horizon (jours). Self-fetching : le pipeline générique /widget/type ne
   * porte pas de query params.
   */
  loadCouvertureCommandes(
    horizonJours: number,
  ): Observable<COUVERTURE_COMMANDES_DATA> {
    const params = new HttpParams().set("horizon", String(horizonJours));
    return this.http.get<COUVERTURE_COMMANDES_DATA>(
      `${environment.api()}/widget/couverture-commandes`,
      { params },
    );
  }

  /**
   * Force un nouveau snapshot du jour (idempotent). Snapshot léger qui
   * utilise les coûts courants. Pour rejouer l'historique avec coûts
   * historiques, voir `runBackfill()`.
   */
  runSnapshot(): Observable<{ snapshotDate: string; rowsInserted: number }> {
    return this.http.post<{ snapshotDate: string; rowsInserted: number }>(
      `${environment.api()}/widget/snapshot/run`,
      {},
    );
  }

  /**
   * Reconstruit les snapshots passés sur la plage demandée en rejouant
   * les mouvements depuis l'état courant et en utilisant les coûts
   * historiques. Idempotent. Coût : 30 s à 2 min selon la base.
   *
   * Plages : `1m | 3m | 6m | 1y | exercice`
   */
  runBackfill(range: BackfillRange): Observable<BackfillResult> {
    const params = new HttpParams().set('range', range);
    return this.http.post<BackfillResult>(
      `${environment.api()}/widget/snapshot/backfill`,
      {},
      { params },
    );
  }
}

export type BackfillRange =
  | "1m"
  | "3m"
  | "6m"
  | "1y"
  | "exercice"
  | "ytd"
  | "exerciceN1";

export interface BackfillResult {
  range: BackfillRange;
  startDate: string;
  endDate: string;
  daysProcessed: number;
  rowsInserted: number;
  mouvementsProcessed: number;
  durationMs: number;
}
